import React, { useCallback } from 'react';
import { setValue, shouldShowField } from '../../../../../components/InputField/helper';
import { WrapperField } from '../../../../../components/InputField/WrapperField';
import InputLabel from '../../../../../components/InputField/InputLabel';
import { observer } from 'mobx-react-lite';

const ACCESS_TYPES = [
    { id: 0, type: "Viewer", text: "View payments and create new beneficiaries." },
    { id: 1, type: "Basic User", text: "All access of Viewer and initiate new payments. Payments initiated by basic users will require authorisation from owner (or) approver (or) a power user before the payment is submitted for further processing. Basic user cannot authorise payments." },
    { id: 2, type: "Approver", text: "All access of Basic User and approve payments initiated by others. Approver’s own initiated transactions will require authorisation from another Approver, Power User or Owner." },
    { id: 3, type: "Power User", text: "Same access as Owner" },
]
const CONTROL_PRONG_LABEL = "Is this person a Control Prong?";
const CONTROL_PRONG_TOOLTIP = "A Control Prong is an individual with significant responsibility to control, manage, or direct the legal entity, such as an executive officer, senior manager, or another individual who regularly performs similar functions.";
const CONTROL_PRONG_DIRECTOR_LABEL = "Is this person a Director/CEO/Authorised Signatory of the entity?";

const compactAddressField = (fieldMap, fieldKey) => {
    const jsonField = fieldMap?.[fieldKey];
    return jsonField ? { ...jsonField, noLabel: true } : jsonField;
};

const User = ({
    asLabel,
    data,
    handleChange,
    hasError,
    index,
    cyclosFields,
    setErrors,
    iPlusFields,
    isFieldEditable,
    isForEDD,
    applicationForm,
    isEditable = true,
    readOnlyMessage = "",
    documentsSlot,
}) => {
    const renderDocumentsSlot = () => {
        if (!documentsSlot) return null;
        if (typeof documentsSlot !== 'function') return documentsSlot;
        return documentsSlot({ ownerId: data?.individuals?.[index]?._id, index });
    };

    const getConfiguredField = useCallback((fieldName) => cyclosFields?.[fieldName] || null, [cyclosFields]);
    const shouldShowConfiguredField = useCallback((fieldName) => {
        const fieldConfig = getConfiguredField(fieldName);
        if (!fieldConfig) {
            return false;
        }
        return shouldShowField(data, fieldConfig?.showIf, index);
    }, [data, getConfiguredField, index]);

    const handleFieldError = (field, errorMsg) => {
        setErrors((prevErrors) => {
            if (typeof prevErrors !== 'object' || prevErrors === null) {
                return { [field]: errorMsg };
            }
            const currentValue = prevErrors?.[field];
            if (currentValue === errorMsg) {
                return prevErrors;
            }
            const updatedErrors = { ...prevErrors };
            updatedErrors[field] = errorMsg;
            return updatedErrors;
        });
    };

    const documentsSlotNode = renderDocumentsSlot();

    const canEdit = !asLabel && isEditable;
    const wrapperModeProps = { asDisplay: !canEdit, disabled: !canEdit };
    const noop = useCallback(() => { }, []);
    const individual = data?.individuals?.[index] || {};
    const applicationType = Number(applicationForm?.type);
    const isPlusFlow = applicationType === 1;
    const supportsControlPersonFlow = [1, 2, 3].includes(applicationType) && Boolean(cyclosFields?.["individuals[].isControlPerson"]);
    const isControlPerson = supportsControlPersonFlow && individual?.isControlPerson === true;
    const isShareHolder = Boolean(individual?.isShareHolder);
    const isAuthorizedUser = Boolean(individual?.isAuthorizedUser);
    const shouldShowIndependentDirector = !isShareHolder && !isAuthorizedUser && !isControlPerson;
    const shouldShowIndividualDetails = shouldShowConfiguredField("individuals[].firstName")
        || isShareHolder
        || isAuthorizedUser
        || isControlPerson
        || Boolean(individual?.isIndependentDirector);
    const shouldShowRelationshipFields = isControlPerson
        || shouldShowConfiguredField("individuals[].isDirector");
    const shouldShowAuthorizedOnlyFields = shouldShowConfiguredField("individuals[].isAuthorizedContact")
        || shouldShowConfiguredField("individuals[].isAgentWithAttorneyPower");
    const controlProngJsonField = supportsControlPersonFlow ? {
        ...cyclosFields?.["individuals[].isControlPerson"],
        label: CONTROL_PRONG_LABEL,
        error: `Please select ${CONTROL_PRONG_LABEL}`,
        tooltip: CONTROL_PRONG_TOOLTIP,
        errorTypes: {
            ...(cyclosFields?.["individuals[].isControlPerson"]?.errorTypes || {}),
            required: `Please select ${CONTROL_PRONG_LABEL}`,
        },
    } : null;
    const directorJsonField = isControlPerson ? {
        ...cyclosFields?.["individuals[].isDirector"],
        label: CONTROL_PRONG_DIRECTOR_LABEL,
        error: `Please select ${CONTROL_PRONG_DIRECTOR_LABEL}`,
    } : cyclosFields?.["individuals[].isDirector"];

    const computeFieldMode = useCallback((fieldSuffix) => {
        if (!canEdit) {
            return { editable: false, disabled: true, asDisplay: true };
        }

        if (!isFieldEditable) {
            return { editable: true, disabled: false, asDisplay: false };
        }

        const editable = isFieldEditable(`individuals[].${fieldSuffix}`, { arrayIndexing: index });
        const isEditable = Boolean(editable);
        return {
            editable: isEditable,
            disabled: !isEditable,
            asDisplay: !isEditable,
        };
    }, [canEdit, index, isFieldEditable]);

    const combineMode = useCallback((mode = {}) => {
        const disabled = wrapperModeProps.disabled || Boolean(mode.disabled);
        const asDisplay = wrapperModeProps.asDisplay || Boolean(mode.asDisplay);
        const editable = !disabled && mode.editable !== false;
        return { disabled, asDisplay, editable };
    }, [wrapperModeProps.asDisplay, wrapperModeProps.disabled]);

    const isPrimaryUser = Boolean(data?.individuals?.[index]?.isPrimaryUser) || index === 0;
    const lockPrimaryAccessFields = isForEDD && isPrimaryUser;
    const isPrimaryUserFixedForPlus = isPlusFlow && isPrimaryUser;

    const rawEmailMode = combineMode(computeFieldMode('email'));
    const emailMode = isPrimaryUserFixedForPlus
        ? { ...rawEmailMode, disabled: true, asDisplay: false, editable: false }
        : rawEmailMode;
    const mobilePhoneMode = combineMode(computeFieldMode('mobilePhone'));
    const motherMaidenMode = combineMode(computeFieldMode('Mother_Maiden_Name'));
    const percentOwnerMode = combineMode(computeFieldMode('percentOwner'));
    const isShareHolderMode = combineMode(computeFieldMode('isShareHolder'));
    const rawIsAuthorizedUserMode = combineMode(computeFieldMode('isAuthorizedUser'));
    const isAuthorizedUserMode = (lockPrimaryAccessFields || isPrimaryUserFixedForPlus)
        ? { ...rawIsAuthorizedUserMode, disabled: true, asDisplay: false, editable: false }
        : rawIsAuthorizedUserMode;
    const rawAccessTypeMode = combineMode(computeFieldMode('accessType'));
    const accessTypeMode = (lockPrimaryAccessFields || isPrimaryUserFixedForPlus)
        ? { ...rawAccessTypeMode, disabled: true, asDisplay: false, editable: false }
        : rawAccessTypeMode;

    const normalizeRoleToggleUpdate = useCallback((updatedData, fieldName) => {
        let nextData = updatedData;
        const person = nextData?.individuals?.[index];
        if (!person) {
            return nextData;
        }

        if ((fieldName === 'isShareHolder' || fieldName === 'isAuthorizedUser' || fieldName === 'isControlPerson') && person?.[fieldName] === true) {
            nextData = setValue(nextData, `individuals[${index}].isIndependentDirector`, false, isForEDD);
        }

        const nextPerson = nextData?.individuals?.[index];
        if (fieldName === 'isAuthorizedUser' && nextPerson?.isAuthorizedUser === true && !nextPerson?.accessType) {
            nextData = setValue(nextData, `individuals[${index}].accessType`, "NoGroup", isForEDD);
        }

        if (
            (fieldName === 'isShareHolder' || fieldName === 'isAuthorizedUser' || fieldName === 'isControlPerson')
            && nextPerson?.isShareHolder !== true
        ) {
            nextData = setValue(nextData, `individuals[${index}].percentOwner`, null, isForEDD);
        }

        return nextData;
    }, [index, isForEDD]);

    const applyNestedUpdates = useCallback((nextData, updates = []) => {
        if (!updates.length) {
            handleChange(nextData);
            return;
        }

        const patchedData = updates.reduce((draft, { fieldPath, value }) => (
            setValue(draft, fieldPath, value, isForEDD)
        ), nextData);

        handleChange(patchedData);
    }, [handleChange, isForEDD]);

    const handleAddressCountryChange = useCallback((nextData) => {
        const nextCountry = nextData?.individuals?.[index]?.address?.country;
        const nextNationality = nextData?.individuals?.[index]?.nationality;
        const hasUsTie = ["US", "USA"].includes(nextCountry) || ["US", "USA"].includes(nextNationality);

        applyNestedUpdates(nextData, hasUsTie ? [] : [{
            fieldPath: `individuals[${index}].ssn`,
            value: null,
        }]);
    }, [applyNestedUpdates, index]);

    const handleNationalityChange = useCallback((nextData) => {
        const nextNationality = nextData?.individuals?.[index]?.nationality;
        const nextCountryOfIssuance = nextData?.individuals?.[index]?.countryOfIssuance;
        const nextCountry = nextData?.individuals?.[index]?.address?.country;
        const hasUsTie = ["US", "USA"].includes(nextNationality) || ["US", "USA"].includes(nextCountry);
        const updates = [];

        if (!nextCountryOfIssuance) {
            updates.push({
                fieldPath: `individuals[${index}].countryOfIssuance`,
                value: nextNationality,
            });
        }

        if (!hasUsTie) {
            updates.push({
                fieldPath: `individuals[${index}].ssn`,
                value: null,
            });
        }

        applyNestedUpdates(nextData, updates);
    }, [applyNestedUpdates, index]);

    return (
        <div className='mt-2'>
            {!canEdit && readOnlyMessage && (
                <div className='text-muted small fst-italic mb-2'>{readOnlyMessage}</div>
            )}
            <WrapperField
                disabled={isShareHolderMode.disabled}
                asDisplay={isShareHolderMode.asDisplay}
                jsonField={cyclosFields["individuals[].isShareHolder"]}
                arrayIndexing={index}
                dataObj={data}
                onChange={(updatedData) => handleChange(normalizeRoleToggleUpdate(updatedData, 'isShareHolder'))}
                showError={isShareHolderMode.editable ? hasError : false}
                onError={isShareHolderMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].isShareHolder`, errorMsg) : noop}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={isAuthorizedUserMode.disabled}
                asDisplay={isAuthorizedUserMode.asDisplay}
                jsonField={cyclosFields["individuals[].isAuthorizedUser"]}
                arrayIndexing={index}
                dataObj={data}
                onChange={(updatedData) => handleChange(normalizeRoleToggleUpdate(updatedData, 'isAuthorizedUser'))}
                showError={isAuthorizedUserMode.editable ? hasError : false}
                onError={isAuthorizedUserMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].isAuthorizedUser`, errorMsg) : noop}
                isForEDD={isForEDD}
                defaultValue={index === 0 ? true : null}
            />

            {supportsControlPersonFlow ? (
                <WrapperField
                    {...wrapperModeProps}
                    jsonField={controlProngJsonField}
                    arrayIndexing={index}
                    dataObj={data}
                    onChange={(updatedData) => handleChange(normalizeRoleToggleUpdate(updatedData, 'isControlPerson'))}
                    showError={hasError}
                    onError={(errorMsg) => handleFieldError(`individuals[${index}].isControlPerson`, errorMsg)}
                    isForEDD={isForEDD}
                />
            ) : null}

            {shouldShowIndependentDirector && (
                <WrapperField
                    {...wrapperModeProps}
                    jsonField={cyclosFields["individuals[].isIndependentDirector"]}
                    arrayIndexing={index}
                    dataObj={data}
                    onChange={handleChange}
                    showError={hasError}
                    onError={(errorMsg) => handleFieldError(`individuals[${index}].isIndependentDirector`, errorMsg)}
                    isForEDD={isForEDD}
                />
            )}

            {shouldShowIndividualDetails && (
                <div className='row'>
                    <InputLabel required label={"Name"} labelStyle={{ fontSize: 15 }} />
                    <p className='ps-3 ms-0 mb-0' style={{ fontSize: 11, fontStyle: 'italic' }}>
                        Please enter your name exactly as it appears on your passport.
                    </p>
                    <WrapperField
                        className="col-sm-4"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].firstName"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].firstName`, errorMsg)}
                        isForEDD={isForEDD}
                    />
                    <WrapperField
                        className="col-sm-4"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].middleName"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].middleName`, errorMsg)}
                        isForEDD={isForEDD}
                    />
                    <WrapperField
                        className="col-sm-4"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].lastName"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].lastName`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <InputLabel required label={"Address"} labelStyle={{ fontSize: 15 }} />
                    <WrapperField
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].address.street"]}
                        arrayIndexing={index}
                        dataObj={data}
                        haveDependentSubFields
                        onChange={handleChange}
                        customProp={iPlusFields?.filter(field => field?.parentField === "individuals[].address")}
                        showError={hasError}
                        onError={(errorMsg, streetNameLabel) => {
                            const label = streetNameLabel || `individuals[${index}].address.street`;
                            handleFieldError(label, errorMsg);
                        }}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        {...wrapperModeProps}
                        jsonField={compactAddressField(cyclosFields, "individuals[].address.buildingNumber")}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].address.buildingNumber`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6 col-md-3"
                        {...wrapperModeProps}
                        jsonField={compactAddressField(cyclosFields, "individuals[].address.city")}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].address.city`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6 col-md-3"
                        {...wrapperModeProps}
                        jsonField={compactAddressField(cyclosFields, "individuals[].address.state")}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].address.state`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    {!data?.individuals?.[index]?.address?.hidePinCode && (
                        <WrapperField
                            className="col-sm-6 col-md-3"
                            {...wrapperModeProps}
                            jsonField={compactAddressField(cyclosFields, "individuals[].address.pinCode")}
                            arrayIndexing={index}
                            dataObj={data}
                            onChange={handleChange}
                            showError={hasError}
                            onError={(errorMsg) => handleFieldError(`individuals[${index}].address.pinCode`, errorMsg)}
                            isForEDD={isForEDD}
                        />
                    )}

                    <WrapperField
                        className="col-sm-6 col-md-3 address-country-picker"
                        {...wrapperModeProps}
                        jsonField={compactAddressField(cyclosFields, "individuals[].address.country")}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleAddressCountryChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].address.country`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    {/* When the postal code auto-hides (country with a fixed default, e.g. Hong Kong
                        -> 999077) the country field shifts left into its slot; this same-width spacer
                        takes the trailing slot so the following field (Date of Birth) stays on its own
                        row instead of reflowing up (WrapperFields grow via flex-1). */}
                    {data?.individuals?.[index]?.address?.hidePinCode && (
                        <div className="col-sm-6 col-md-3" aria-hidden="true" />
                    )}

                    <WrapperField
                        className="col-sm-6"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].dob"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].dob`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6  mt-2"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].nationality"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleNationalityChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].nationality`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6 mt-2"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].Country_Of_Birth"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].Country_Of_Birth`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6"
                        disabled={emailMode.disabled}
                        asDisplay={emailMode.asDisplay}
                        jsonField={cyclosFields["individuals[].email"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={emailMode.editable ? hasError : false}
                        onError={emailMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].email`, errorMsg) : noop}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6 mt-2"
                        disabled={mobilePhoneMode.disabled}
                        asDisplay={mobilePhoneMode.asDisplay}
                        jsonField={cyclosFields["individuals[].mobilePhone"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={mobilePhoneMode.editable ? hasError : false}
                        onError={mobilePhoneMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].mobilePhone`, errorMsg) : noop}
                        isForEDD={isForEDD}
                        customProp={{
                            individual_id: data?.individuals?.[index]?._id,
                            validateOnly: !data?.individuals?.[index]?.isAuthorizedUser,
                        }}
                    />

                    <WrapperField
                        className="col-sm-6"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].taxId"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].taxId`, errorMsg)}
                        isForEDD={isForEDD}
                    />


                    <WrapperField
                        className="col-sm-6"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].countryOfIssuance"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].countryOfIssuance`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].role"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].role`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6"
                        disabled={accessTypeMode.disabled}
                        asDisplay={accessTypeMode.asDisplay}
                        jsonField={cyclosFields["individuals[].accessType"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={accessTypeMode.editable ? hasError : false}
                        onError={accessTypeMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].accessType`, errorMsg) : noop}
                        isForEDD={isForEDD}
                    />

                    {data?.individuals?.[index]?.isAuthorizedUser ? <div>
                        {
                            ACCESS_TYPES.map(e => (
                                <div className='my-2 ps-2 fs-6' key={e.id}>
                                    <span className="bold-text">{e.type}: </span>{e.text}
                                </div>
                            ))
                        }
                    </div> : null}

                    <WrapperField
                        className="col-sm-6"
                        disabled={motherMaidenMode.disabled}
                        asDisplay={motherMaidenMode.asDisplay}
                        jsonField={cyclosFields["individuals[].Mother_Maiden_Name"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={motherMaidenMode.editable ? hasError : false}
                        onError={motherMaidenMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].Mother_Maiden_Name`, errorMsg) : noop}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6"
                        disabled={percentOwnerMode.disabled}
                        asDisplay={percentOwnerMode.asDisplay}
                        jsonField={cyclosFields["individuals[].percentOwner"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        requiredOverride={isShareHolder}
                        showError={percentOwnerMode.editable ? hasError : false}
                        onError={percentOwnerMode.editable ? (errorMsg) => handleFieldError(`individuals[${index}].percentOwner`, errorMsg) : noop}
                        isForEDD={isForEDD}
                    />

                    <WrapperField
                        className="col-sm-6"
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].ssn"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].ssn`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    {shouldShowRelationshipFields && (
                        <>
                            <WrapperField
                                className=""
                                {...wrapperModeProps}
                                jsonField={directorJsonField}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={handleChange}
                                showError={hasError}
                                onError={(errorMsg) => handleFieldError(`individuals[${index}].isDirector`, errorMsg)}
                                isForEDD={isForEDD}
                            />
                        </>
                    )}

                    {shouldShowAuthorizedOnlyFields && (
                        <>
                            <WrapperField
                                className=""
                                {...wrapperModeProps}
                                jsonField={cyclosFields["individuals[].isAuthorizedContact"]}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={handleChange}
                                showError={hasError}
                                onError={(errorMsg) => handleFieldError(`individuals[${index}].isAuthorizedContact`, errorMsg)}
                                isForEDD={isForEDD}
                            />

                            <WrapperField
                                className=""
                                {...wrapperModeProps}
                                jsonField={cyclosFields["individuals[].isAgentWithAttorneyPower"]}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={handleChange}
                                showError={hasError}
                                onError={(errorMsg) => handleFieldError(`individuals[${index}].isAgentWithAttorneyPower`, errorMsg)}
                                isForEDD={isForEDD}
                            />
                        </>
                    )}

                    <WrapperField
                        {...wrapperModeProps}
                        jsonField={cyclosFields["individuals[].sourceOfWealthList"]}
                        arrayIndexing={index}
                        dataObj={data}
                        onChange={handleChange}
                        requiredOverride={isShareHolder}
                        showError={hasError}
                        onError={(errorMsg) => handleFieldError(`individuals[${index}].sourceOfWealthList`, errorMsg)}
                        isForEDD={isForEDD}
                    />

                    {/* Add other conditional fields... */}
                </div>
            )}

            {/* A person with no details block (not a shareholder, authorized user,
                control person or independent director) still needs their documents in
                their card. Render the slot in its own full-width row so its border and
                note span the card; a bare child of the details `row` would be an
                auto-width flex item. renderDocumentsSlot() returns null when no
                documentsSlot prop is passed, which keeps the legacy
                BusinessInformation.js path unchanged. */}
            {documentsSlotNode ? (
                <div className='row'>
                    <div className='col-12' data-testid='business-v2-user-documents-slot'>
                        {documentsSlotNode}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default observer(User);
